// Carousel — renders only when the selected subcategory is CAROUSEL (state.js's isCarousel).
// One media type (IMAGE/VIDEO) for the whole carousel, then 1-10 cards, each with its own media,
// a 160-char description and up to 2 buttons drawn from the narrower CAROUSEL_BUTTON_TYPE set.
//
// Wire shapes below are payload.js's own buildCarouselCard/buildCarouselCardButton, read there
// rather than guessed:
//   card.description.value  -> body.text
//   card.variable[].value   -> body.example[]   (kept in sync with the {{n}} tokens typed into
//                                                 card.description.value by the token-watching
//                                                 effect below (cardVariableSyncKey) — same
//                                                 approach sections/Variables.js uses for
//                                                 header/body, just generalised to a per-card list
//                                                 here. Each card renders its own sample-value
//                                                 input per row, same shape as the top-level
//                                                 Variables card.)
//   card.cardMedia.mediaId  -> header.id         (a real upload id, not a multipart file part)
//   card.buttons[]          -> buttons[]         (each via buildCarouselCardButton)
// buildCarouselCardButton:
//   btn.type                                          -> type
//   btn.text.value                                    -> text       (omitted when blank)
//   btn.urlType.value    (type === URL only)          -> urlType
//   btn.buttonValue.value (type === URL only)         -> url
//   btn.buttonValue.value + btn.suffix.value, joined, when urlType.value === 'DYNAMIC' -> example[0]
//   btn.buttonValue.value (type === PHONE_NUMBER only) -> phone_number
// state.carousels.mediaType -> top-level payload.carouselMediaType.
//
// DESIGN DECISION — immediate upload, not deferred: every other media-bearing section in this
// form (see Header.js) stores a picked File on state and lets it ride as a multipart part at
// template create/update time. Carousel cards cannot do that: payload.js's buildCarouselCard
// sends `header: { id: card.cardMedia.mediaId }` — the DTO wants an id that already exists
// server-side, not a file part. So each card's FileInput uploads immediately via
// api/templates.js's uploadTemplateMedia the moment a valid file is picked, and stores the id
// that call returns (EnterpriseMutationResponse: `{ id, status, details }` — `id` is what becomes
// cardMedia.mediaId) rather than waiting for the form's final submit.
import { useEffect, useRef, useState } from 'react';
import {
  Accordion, Alert, Button, FileInput, Icon, Select, TextField, Tooltip, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import {
  CAROUSEL_BUTTON_TYPE, CAROUSEL_MEDIA_TYPE, MEDIA_RULES, URL_TYPE, URL_TYPE_LINK_TRACK_LABEL,
  LINK_TRACKING_NOTE, createButton, createCarouselCard, createId, extractTokens, isCarousel, isLinkTracked,
} from '../state';
import { templateErrorMessage, uploadTemplateMedia } from '../../../../api/templates';
import { checkDynamicUrlBase, checkPhoneFormat, checkUrlFormat } from '../validate';
import { errorProps, liveErrorProps } from './Buttons';
import { friendlyError } from './errorMessages';
import EmojiPickerButton from './EmojiPickerButton';

// Same source file's TEMPLATE_LIMITS — only the fields this section needs.
const CAROUSEL_LIMITS = {
  cardsMin: 1,
  cardsMax: 10,
  buttonsPerCardMax: 2,
  cardBodyMaxChars: 160,
  buttonTextMaxChars: 25,
};

// Console's carousel-card button value caps (gs-ui-templates CreateTemplate/config.ts) — URL and
// phone number carry different limits, so each gets its own constant.
const CAROUSEL_URL_VALUE_MAX_CHARS = 2000;
const CAROUSEL_PHONE_VALUE_MAX_CHARS = 25;

// Same regex/max-based numbering as sections/Body.js's own nextVariableToken — duplicated rather
// than imported, since Body.js's edit scope for this task is restricted to extracting the emoji
// picker only; exporting an unrelated helper from it would exceed that. Counts the {{n}} tokens
// already typed in THIS card's own description text and returns the next one — same rule the
// console's CarouselEditor.onAddVariable applies (gs-ui-templates Carousels/CarouselEditor.tsx:
// 101-103), just max-based here instead of count-based so a gap left by a removed token is never
// reused, consistent with Body.js's own choice.
function nextCardVariableToken(text) {
  const numbers = (text.match(/\{\{(\d+)\}\}/g) || []).map((token) => Number(token.slice(2, -2)));
  const max = numbers.length ? Math.max(...numbers) : 0;
  return `{{${max + 1}}}`;
}

// Mirrors sections/Variables.js's own (unexported) mergeVariables/sameTokens — duplicated rather
// than imported, same reasoning as nextCardVariableToken above duplicating Body.js's helper:
// Variables.js is out of this task's edit scope and neither function is exported from it. The
// actual token PARSER (extractTokens) is not duplicated — it's imported from ../state above, the
// one place it's defined, same as Variables.js itself does.
//
// mergeCardVariables preserves the row (value AND any previously-set error) for a token that's
// still present, drops the row for a token that's gone, and appends a fresh blank
// {name, value:'', error:''} row for a newly-typed token — one row per distinct token, same
// order as `tokens` (document order, per extractTokens).
function mergeCardVariables(existingRows, tokens) {
  const byName = new Map(existingRows.map((row) => [row.name, row]));
  return tokens.map((token) => byName.get(token) || { name: token, value: '', error: '' });
}

// True when `rows` already reflects exactly `tokens`, in the same order — the no-op guard the
// sync effect below uses to decide whether a dispatch is actually needed.
function sameCardTokens(rows, tokens) {
  return rows.length === tokens.length && rows.every((row, index) => row.name === tokens[index]);
}

// A programmatic insert (variable token or emoji) is not blocked by the description TextField's
// own maxLength attribute — that only constrains direct typing — so both insertion paths below
// check this first and silently no-op rather than let the description exceed its 160-char cap.
function wouldExceedCardMax(text, insertion) {
  return text.length + insertion.length > CAROUSEL_LIMITS.cardBodyMaxChars;
}

// MEDIA_RULES' own maxSizeLabel has no space before the unit ("5MB") — gs-ui-templates' copy
// always has one ("File size should be max 5 MB"). Shared by the FileInput hint and the
// size-exceeded error below so the two can't drift on formatting.
function spacedMaxSizeLabel(label) {
  return label ? label.replace(/MB$/, ' MB') : '';
}

// Console-exact copy (gs-ui-templates CreateTemplate/config.ts's en.json
// uploaded_file_should_be_in_jpg/uploaded_file_should_be_in_mp4), keyed by our own carousel media
// type since a carousel only ever offers IMAGE/VIDEO.
const WRONG_MEDIA_FORMAT_MESSAGE = {
  [CAROUSEL_MEDIA_TYPE.IMAGE]: 'Uploaded file should be in JPG, JPEG or PNG format',
  [CAROUSEL_MEDIA_TYPE.VIDEO]: 'Uploaded file should be in MP4 format',
};

// DESIGN DECISION — 30-minute upload expiry: `upload_whatsapp_media` (the endpoint
// api/templates.js's uploadTemplateMedia calls) issues an id that expires 30 minutes after
// upload. A client-side timer can't know the server's clock precisely, so this is a pragmatic
// mitigation, not a guarantee: once a card's own upload passes 25 minutes old, show a static
// warning with a one-click re-upload (same file, same call) rather than a live countdown.
const MEDIA_EXPIRY_WARNING_MS = 25 * 60 * 1000;

const MEDIA_TYPE_OPTIONS = [
  { value: CAROUSEL_MEDIA_TYPE.IMAGE, label: 'Image' },
  { value: CAROUSEL_MEDIA_TYPE.VIDEO, label: 'Video' },
].map((item) => ({
  ...item,
  'data-testid': `bloomreach-templates-form-carousel-media-type-option-${item.value.toLowerCase()}`,
}));

// Console's own carousel button-type list omits Order details; ours keeps it on purpose — our
// backend's CarouselCardButtonType enum still includes ORDER_DETAILS, so dropping it here would
// remove a type our wire supports.
// Labels are console-exact (C/config.ts:544-560 -> en.json quick_reply/url/phone_number) for the
// three types the console also offers; Order Details is our own addition (see the comment above)
// so it borrows the same title-case convention rather than a console string that doesn't exist.
const CAROUSEL_BUTTON_TYPE_OPTIONS = [
  { value: CAROUSEL_BUTTON_TYPE.QUICK_REPLY, label: 'Quick Reply' },
  { value: CAROUSEL_BUTTON_TYPE.URL, label: 'URL' },
  { value: CAROUSEL_BUTTON_TYPE.PHONE_NUMBER, label: 'Phone Number' },
  { value: CAROUSEL_BUTTON_TYPE.ORDER_DETAILS, label: 'Order Details' },
].map((item) => ({
  ...item,
  'data-testid': `bloomreach-templates-form-carousel-button-type-option-${item.value.toLowerCase()}`,
}));

// Labels for a button TYPE this Select doesn't offer — CAROUSEL_BUTTON_TYPE_OPTIONS above only
// covers the four types a user can actually PICK here, but prefill.js's reconstructButton (the
// same one the top-level Buttons section relies on) accepts the whole BUTTON_TYPE vocabulary when
// reading an existing card's buttons back, so an edited card can arrive here holding one of the
// rest. Mirrors Buttons.js's own BUTTON_TYPE_LABELS, duplicated rather than imported since that
// map is private to Buttons.js.
const OTHER_BUTTON_TYPE_LABELS = {
  COPY_CODE: 'Copy offer code',
  FLOW: 'Flow',
  VOICE_CALL: 'Call to WhatsApp',
  REQUEST_CONTACT_INFO: 'Request Contact Info',
  OTP: 'OTP',
  PASSKEY: 'Passkey',
};

// Same fix as Buttons.js's own buildTypeOptions / Header.js's buildHeaderTypeOptions: a Select's
// renderValue looks `value` up in `items` regardless of `disabled`, so a card button whose type
// isn't one of the four offered above would otherwise render blank instead of its real type.
// Append it, disabled, so it's found and displayed but stays unpickable.
function buildCardButtonTypeOptions(currentType) {
  if (Object.values(CAROUSEL_BUTTON_TYPE).includes(currentType)) {
    return CAROUSEL_BUTTON_TYPE_OPTIONS;
  }
  return [
    ...CAROUSEL_BUTTON_TYPE_OPTIONS,
    {
      value: currentType,
      label: OTHER_BUTTON_TYPE_LABELS[currentType] || currentType,
      disabled: true,
      'data-testid': `bloomreach-templates-form-carousel-button-type-option-${String(currentType).toLowerCase()}`,
    },
  ];
}

// Offered to every card URL button, same as the top-level CTA's own URL_TYPE_OPTIONS
// (Buttons.js). Previously withheld here on the theory that the widget's carousel send path
// wasn't verified for link-tracked card buttons — moot: the Message tab hides carousel templates
// entirely (src/pages/Message/index.js:87), so there is no widget send path to protect. Enterprise
// runs the identical per-account link-tracking check on a card's own URL button
// (ce-api ValidationUtils.java:1063-1074 -> URLButton.validate, the same validator the top-level
// CTA goes through). An existing card button already at this option (prefill.js) renders
// identically either way.
const URL_TYPE_OPTIONS = [
  { value: URL_TYPE.STATIC, label: 'Static' },
  { value: URL_TYPE.DYNAMIC, label: 'Dynamic' },
  { value: URL_TYPE.DYNAMIC_LINK_TRACK, label: URL_TYPE_LINK_TRACK_LABEL },
].map((item) => ({
  ...item,
  'data-testid': `bloomreach-templates-form-carousel-url-type-option-${item.value.toLowerCase()}`,
}));

// Live, per-keystroke buttonValue format check for carousel-card buttons — mirrors validateCarousel's
// own type->checker mapping in validate.js exactly (URL/PHONE_NUMBER only; QUICK_REPLY/ORDER_DETAILS
// have no buttonValue to check).
const checkerByType = {
  [CAROUSEL_BUTTON_TYPE.URL]: checkUrlFormat,
  [CAROUSEL_BUTTON_TYPE.PHONE_NUMBER]: checkPhoneFormat,
};

// One card's own media, rendered inline instead of just its filename — mirrors gs-ui-templates'
// CarouselsCard renderMedia. Sourced from the File object already sitting in that card's
// cardMedia.file (uploadCardMedia's response never carries a playable URL, so a freshly picked
// upload can only ever preview from its own File), falling back to cardMedia.mediaSrc — the
// existing media's own CDN URL prefill.js now populates on an edit open with no freshly picked
// file yet. Module-scope, not nested inside CarouselSection, so it's the same component type
// across renders — a component redefined every render would remount on every keystroke and thrash
// every card's object URL. One instance per card (see the cards.map() call site below, inside a
// div keyed on card.cardId), each owning its own effect below, so a reorder/delete/unmount only
// ever revokes the one URL that instance itself created — never a neighbour's.
const CarouselCardMediaPreview = ({
  file, mediaSrc, mediaType, index, currentMediaCaption,
}) => {
  const [previewUrl, setPreviewUrl] = useState('');

  // Same pattern as sections/Header.js's own pickedFile/previewUrl effect for its single media
  // field: createObjectURL keyed on the File itself, never called inline in JSX (that would mint
  // a new, unrevoked URL every render). The cleanup runs before the next effect — file replaced or
  // cleared — and once more on unmount, whether that's this card being removed or the form itself
  // closing.
  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Edit-mode fallback: once a card is prefilled from an existing template with no freshly picked
  // file yet, `mediaSrc` is that card's own existing media CDN URL — used directly as a URL,
  // never routed through createObjectURL/revokeObjectURL (that pair above only ever touches
  // `file`, a real File object). previewUrl wins whenever a file IS picked; the caller below only
  // ever renders this component from a branch keyed on whichever of file/mediaSrc actually applies,
  // so in practice the two never really compete — the `||` just makes that precedence explicit.
  const currentSrc = previewUrl || mediaSrc || '';
  // A remote mediaSrc can fail to load (expired link, CDN hiccup, cross-origin block) in a way a
  // freshly created blob URL practically never does — resets whenever the src being shown changes
  // so a stale failure doesn't keep hiding a perfectly good new preview.
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    setLoadError(false);
  }, [currentSrc]);

  // Covers "no file and no mediaSrc yet", the one render frame before the effect above has run
  // for a brand new file, and a src that failed to load — never point an <img>/<video> at a blank
  // or broken src.
  if (!currentSrc || loadError) return null;

  // currentMediaCaption (only ever passed for the mediaSrc-fallback case, never a freshly picked
  // file — see the call site below) is rendered as part of this same return so it can never be
  // left showing above a preview that just hid itself on a load error, or vice versa.
  const caption = currentMediaCaption && (
    <Typography
      variant="caption"
      className="br-tpl-form-helper-text"
      data-testid={`bloomreach-templates-form-carousel-card-${index}-media-current-caption`}
    >
      {currentMediaCaption}
    </Typography>
  );

  if (mediaType === CAROUSEL_MEDIA_TYPE.VIDEO) {
    return (
      <>
        <video
          controls
          className="br-tpl-form-carousel-media-preview"
          onError={() => setLoadError(true)}
          data-testid={`bloomreach-templates-form-carousel-card-${index}-media-preview`}
        >
          {/* file.type, not a hardcoded mime, when previewing a freshly picked file — mirrors
              whatever the browser reported for the picked file. Carousel VIDEO reads the same
              MEDIA_RULES entry Header.js does (state.js — MP4-only, video/3gp was dropped
              2026-09-24, see that file's own comment), and handleCardFileChange already
              whitelists file.type against it before a file ever reaches state. No file
              (previewing mediaSrc instead) means no local mime to read, so the type attribute is
              left off and the browser sniffs it from the response. */}
          <source src={currentSrc} type={file ? file.type : undefined} />
        </video>
        {caption}
      </>
    );
  }

  return (
    <>
      <img
        src={currentSrc}
        alt={file ? `Preview of ${file.name}` : 'Preview of current card media'}
        className="br-tpl-form-carousel-media-preview"
        onError={() => setLoadError(true)}
        data-testid={`bloomreach-templates-form-carousel-card-${index}-media-preview`}
      />
      {caption}
    </>
  );
};

// Carousel — a Select for the whole carousel's media type, then 1-10 accordion cards, each with
// its own media/description/buttons. Only renders for isCarousel(state); needs orgId + channelId
// (beyond every other section's state/dispatch/errors/disabled) so it can upload each card's
// media immediately — see the file-header comment above.
const CarouselSection = ({
  state, dispatch, errors, disabled, orgId, channelId,
}) => {
  // undefined = "no interaction yet" (defaults card 0 open below); null = the user explicitly
  // collapsed every card — these must stay distinguishable, or collapsing card 0 would
  // immediately snap back open on the next render.
  const [expandedCardId, setExpandedCardId] = useState(undefined);
  const [uploadingCardIds, setUploadingCardIds] = useState({});
  // Only the async upload path below needs this. Every other handler here is a single
  // synchronous dispatch and can safely read `state`/`cards` straight from props/closure like
  // every other section does. An in-flight upload spans real wall-clock time though, so by the
  // time it resolves the user may have added/removed/edited other cards; reading the stale
  // `cards` closed over at pick-time would silently discard whatever happened in between. The ref
  // always holds the latest committed state to dereference at resolution time instead.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  // Per-card upload generation counter. uploadCardMedia is fire-and-forget — nothing stops a user
  // from picking a new file for the same card, or hitting re-upload, before the previous request
  // for that card has resolved. Without tracking which request is the latest, a slower request's
  // response can land after a faster, more recent one already applied — silently reverting the
  // card to stale data (or a stale media type). uploadCardMedia bumps this card's number before
  // it starts a request; a response is only applied once resolved if its captured number is still
  // the latest one recorded here, otherwise it's discarded as superseded.
  const uploadGenerationRef = useRef(new Map());
  // Drag-to-reorder (native HTML5 DnD — see the handlers below). draggedCardIdRef holds the id of
  // whichever card's handle most recently fired dragstart; a ref rather than state because
  // starting a drag has nothing on screen that needs to re-render. dragOverCardId is the one thing
  // that does — whichever card the dragged one is currently hovering, so its drop-target wrapper
  // can show the drag-over affordance.
  const draggedCardIdRef = useRef(null);
  const [dragOverCardId, setDragOverCardId] = useState(null);
  // One description-field DOM node per card, keyed by cardId — cardId rather than index since a
  // drag reorder changes indices but never ids. Populated via each description TextField's own
  // inputRef callback below; read by handleAddCardVariable/handleCardEmojiSelect to find that
  // card's own caret position, same purpose textareaRef serves in sections/Body.js, just one per
  // card instead of one for the whole section.
  const descriptionRefs = useRef(new Map());

  const carousel = isCarousel(state);
  const { cards } = state.carousels;

  // validate.js keys every carousel-card error by its index at Submit time
  // (carousels.cards.${index}.*). A drag reorder (or an add/remove) can change
  // state.carousels.cards' order without a fresh validateForm() pass, so a naive
  // `errors[carousels.cards.${liveIndex}...]` lookup would then read a stale error under
  // whichever card happens to occupy that old slot now — a different card's mistake shown on an
  // innocent card. Remember the cardId order `errors` was actually computed against, and look
  // each card's own errors up by where ITS id sat then (cardErrorIndexFor below), not where it
  // sits now. Same approach ButtonsSection (Buttons.js) uses for its own buttons, updated
  // synchronously during render rather than in an effect so a fresh validation pass is
  // attributed correctly on the very render that receives it, not one paint later.
  const [prevErrors, setPrevErrors] = useState(errors);
  const [cardErrorOrder, setCardErrorOrder] = useState(() => cards.map((card) => card.cardId));
  if (errors !== prevErrors) {
    setPrevErrors(errors);
    setCardErrorOrder(cards.map((card) => card.cardId));
  }

  // Keeps the section from ever showing a 0-card list once it's active — there is no other
  // "create the first card" affordance. Category.js resets `carousels` to its blank default
  // ({ mediaType: 'IMAGE', buttonTypes: [], cards: [] }) on both category change and subcategory
  // change (its own handleCategoryChange/handleSubCategoryChange), so a fresh mount of this
  // component — whether this is the first-ever visit to Carousel or a return to it after leaving
  // — should never see stale cards from a previous session. Either way `cards` starts at [], and
  // this effect's own `cards.length === 0` check below is what seeds the first blank card.
  useEffect(() => {
    if (carousel && cards.length === 0) {
      dispatch({ type: 'set', path: 'carousels.cards', value: [createCarouselCard(createId('card'))] });
    }
  }, [carousel, cards.length]);

  // Keeps every card's own card.variable rows in sync with the {{n}} tokens currently typed in
  // its description.value — same token-watching-effect approach sections/Variables.js uses for
  // header.headerText.value/body.value, generalised to a list: one combined effect over every
  // card (instead of one per field) so an add/remove/reorder/edit anywhere in the list only ever
  // needs one dispatch, never N. The dependency key below is built by walking `cards` itself and
  // reading each card's own cardId + description.value — never an index into the array — so a
  // drag reorder (handleCardDrop above) can never be mistaken for a content change: whichever
  // slot a card now sits in, it's still the same object carrying the same description and
  // variable rows it always had.
  const cardVariableSyncKey = cards.map((card) => `${card.cardId}\u0000${card.description.value}`).join('\u0001');
  useEffect(() => {
    if (!carousel) return;
    let changed = false;
    const nextCards = cards.map((card) => {
      const tokens = extractTokens(card.description.value);
      if (sameCardTokens(card.variable, tokens)) return card;
      changed = true;
      return { ...card, variable: mergeCardVariables(card.variable, tokens) };
    });
    // Same no-op guard as Variables.js's own effects: only dispatch when some card's computed
    // rows actually differ from what's already in state. The dependency key is derived from
    // description text alone (never from .variable), so a dispatch here — which only ever
    // touches .variable — can't itself change cardVariableSyncKey and re-trigger this effect.
    if (changed) {
      dispatch({ type: 'set', path: 'carousels.cards', value: nextCards });
    }
    // Deps are cardVariableSyncKey (cardId + raw description text per card) only — re-deriving
    // off card.variable itself would loop, same reasoning as Variables.js's own eslint-disable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardVariableSyncKey]);

  if (!carousel) {
    return null;
  }

  // Where `errors` actually keys this card's mistakes — cardErrorOrder's position for its id, or
  // its own live position when the id has no remembered slot (a card added since the last
  // validateForm() pass never appeared in cardErrorOrder).
  const cardErrorIndexFor = (cardId) => {
    const at = cardErrorOrder.indexOf(cardId);
    return at === -1 ? cards.findIndex((card) => card.cardId === cardId) : at;
  };

  const updateCards = (nextCards) => {
    dispatch({ type: 'set', path: 'carousels.cards', value: nextCards });
  };

  const updateCard = (cardId, updater) => {
    updateCards(cards.map((card) => (card.cardId === cardId ? updater(card) : card)));
  };

  const handleMediaTypeChange = (selected) => {
    const value = Array.isArray(selected) ? selected[0] : selected;
    dispatch({ type: 'set', path: 'carousels.mediaType', value });
    // Every card's existing upload was validated against the OLD media type's mime rule — an
    // id/file left behind after switching would silently point at the wrong kind of media. Same
    // defensive full-replace Header.js does on its own header media-type change.
    updateCards(cards.map((card) => ({
      ...card,
      cardMedia: { value: '', mediaSrc: '', mediaId: '', error: '', file: null },
      mediaUploadedAt: undefined,
    })));
  };

  const handleAddCard = () => {
    if (cards.length >= CAROUSEL_LIMITS.cardsMax) return;
    const card = createCarouselCard(createId('card'));
    updateCards([...cards, card]);
    setExpandedCardId(card.cardId);
  };

  const handleRemoveCard = (cardId) => {
    if (cards.length <= CAROUSEL_LIMITS.cardsMin) return;
    updateCards(cards.filter((card) => card.cardId !== cardId));
  };

  // Drag-to-reorder — the handle (rendered per card below) is the only draggable element, never
  // the card itself: a card holds text inputs, and a draggable card body fights text selection.
  // These four handlers split across the handle (drag start/end) and each card's own wrapper div
  // (drag over/drop, the drop target) at the JSX call site further down.
  const handleCardDragStart = (event, cardId) => {
    if (disabled) return;
    draggedCardIdRef.current = cardId;
    event.dataTransfer.effectAllowed = 'move';
    // Firefox never fires dragstart at all unless setData is called here — the reorder logic below
    // never reads it back (dataTransfer isn't reliably readable until drop; draggedCardIdRef is).
    event.dataTransfer.setData('text/plain', cardId);
  };

  const handleCardDragOver = (event, cardId) => {
    if (disabled) return;
    // Without this the browser's default is to refuse the drop outright and onDrop never fires.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverCardId !== cardId) setDragOverCardId(cardId);
  };

  const handleCardDrop = (event, targetCardId) => {
    if (disabled) return;
    event.preventDefault();
    setDragOverCardId(null);
    const sourceCardId = draggedCardIdRef.current;
    draggedCardIdRef.current = null;
    if (!sourceCardId || sourceCardId === targetCardId) return;
    const movedCard = cards.find((card) => card.cardId === sourceCardId);
    const remaining = cards.filter((card) => card.cardId !== sourceCardId);
    const targetIndex = remaining.findIndex((card) => card.cardId === targetCardId);
    if (!movedCard || targetIndex === -1) return;
    // Fresh array (filter + slice, never splice on `cards` itself) inserted before the target,
    // then the same 'set' dispatch every other card-list edit in this file already uses.
    updateCards([...remaining.slice(0, targetIndex), movedCard, ...remaining.slice(targetIndex)]);
  };

  // Fires on the handle once the drag ends, however it ends — a completed drop, one dropped
  // outside any card, or the user hitting Escape. Whichever of the above already ran (or didn't),
  // this is the one place both pieces of drag state are guaranteed to end up clear.
  const handleCardDragEnd = () => {
    draggedCardIdRef.current = null;
    setDragOverCardId(null);
  };

  const handleDescriptionChange = (cardId, value) => {
    updateCard(cardId, (card) => ({ ...card, description: { value, error: '' } }));
  };

  // One row's own sample value, by its position in card.variable. No live format check here —
  // same rule sections/Variables.js applies to body's own rows (only HEADER variables get one
  // there), and the console's CAROUSEL-field Variables.tsx never sets an error on this field
  // either. validate.js's own submit-time sample_variable_is_required is what actually gates it,
  // surfaced below via errorProps exactly like this card's other fields already do.
  const handleCardVariableChange = (cardId, varIndex, value) => {
    updateCard(cardId, (card) => ({
      ...card,
      variable: card.variable.map((row, index) => (index === varIndex ? { ...row, value, error: '' } : row)),
    }));
  };

  // Inserts the next {{n}} token at this card's own caret — disabled state at the call site below
  // already rules out an overflow, but the check is repeated here too since a click can still land
  // a frame after state changed underneath it (e.g. another field's error clearing mid-render).
  // Goes through handleDescriptionChange, the exact same path typing into the field already uses,
  // so this is indistinguishable from having typed the token by hand.
  const handleAddCardVariable = (cardId) => {
    const card = cards.find((c) => c.cardId === cardId);
    if (!card) return;
    const text = card.description.value;
    const token = nextCardVariableToken(text);
    if (wouldExceedCardMax(text, token)) return;
    const el = descriptionRefs.current.get(cardId);
    const pos = el ? (el.selectionStart ?? text.length) : text.length;
    const next = `${text.slice(0, pos)}${token}${text.slice(pos)}`;
    handleDescriptionChange(cardId, next);
    if (el) {
      const caretPos = pos + token.length;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caretPos, caretPos);
      });
    }
  };

  // Same caret-splice as handleAddCardVariable above, but the advance is emoji.length rather than
  // a fixed token length — an emoji is 2+ UTF-16 code units, so advancing by 1 would leave the
  // caret inside it (same reasoning as sections/Body.js's own handleEmojiSelect).
  const handleCardEmojiSelect = (cardId, emoji) => {
    const card = cards.find((c) => c.cardId === cardId);
    if (!card) return;
    const text = card.description.value;
    if (wouldExceedCardMax(text, emoji)) return;
    const el = descriptionRefs.current.get(cardId);
    const pos = el ? (el.selectionStart ?? text.length) : text.length;
    const next = `${text.slice(0, pos)}${emoji}${text.slice(pos)}`;
    handleDescriptionChange(cardId, next);
    if (el) {
      const caretPos = pos + emoji.length;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caretPos, caretPos);
      });
    }
  };

  const handleRemoveMedia = (cardId) => {
    updateCard(cardId, (card) => ({
      ...card,
      cardMedia: { value: '', mediaSrc: '', mediaId: '', error: '', file: null },
      mediaUploadedAt: undefined,
    }));
  };

  // Shared by the initial pick and the "re-upload" affordance below — both just send the file to
  // the same endpoint again.
  const uploadCardMedia = async (cardId, file) => {
    if (!orgId || !channelId) {
      updateCard(cardId, (card) => ({
        ...card,
        cardMedia: { ...card.cardMedia, error: 'Missing organization or channel — cannot upload media.', file: null },
      }));
      return;
    }
    // Captured now, before the request goes out — not re-read after the await. `generation` makes
    // this the newest attempt for `cardId`; `requestMediaType` closes over `state` as it is on this
    // call (this function is invoked synchronously from a user action, so that's "request time"),
    // so it stays the type this exact file was validated/uploaded against even if the carousel's
    // media type changes before the response comes back.
    const generation = (uploadGenerationRef.current.get(cardId) || 0) + 1;
    uploadGenerationRef.current.set(cardId, generation);
    const requestMediaType = state.carousels.mediaType;
    setUploadingCardIds((prev) => ({ ...prev, [cardId]: true }));
    try {
      const res = await uploadTemplateMedia(orgId, channelId, file);
      if (uploadGenerationRef.current.get(cardId) !== generation) return;
      const latestCards = stateRef.current.carousels.cards;
      updateCards(latestCards.map((card) => (card.cardId === cardId
        ? {
          ...card,
          cardMedia: {
            value: requestMediaType, mediaSrc: '', mediaId: res.id, error: '', file,
          },
          mediaUploadedAt: Date.now(),
        }
        : card)));
    } catch (err) {
      if (uploadGenerationRef.current.get(cardId) !== generation) return;
      const latestCards = stateRef.current.carousels.cards;
      updateCards(latestCards.map((card) => (card.cardId === cardId
        ? { ...card, cardMedia: { ...card.cardMedia, error: templateErrorMessage(err), file: null, mediaId: '' } }
        : card)));
    } finally {
      setUploadingCardIds((prev) => {
        const next = { ...prev };
        delete next[cardId];
        return next;
      });
    }
  };

  const handleCardFileChange = (cardId, files) => {
    const file = files && files[0];
    if (!file) return;
    const rule = MEDIA_RULES[state.carousels.mediaType];
    if (!rule) return;
    if (!rule.accept.includes(file.type)) {
      updateCard(cardId, (card) => ({
        ...card,
        cardMedia: { ...card.cardMedia, error: WRONG_MEDIA_FORMAT_MESSAGE[state.carousels.mediaType] },
      }));
      return;
    }
    if (file.size > rule.maxSizeBytes) {
      updateCard(cardId, (card) => ({
        ...card,
        cardMedia: { ...card.cardMedia, error: `File size exceeding maximum limit of ${spacedMaxSizeLabel(rule.maxSizeLabel)}` },
      }));
      return;
    }
    // Optimistic: show the filename immediately, confirm/revert once uploadCardMedia settles.
    updateCard(cardId, (card) => ({
      ...card,
      cardMedia: { ...card.cardMedia, file, error: '', mediaId: '' },
      mediaUploadedAt: undefined,
    }));
    uploadCardMedia(cardId, file);
  };

  const handleAddButton = (cardId) => {
    updateCard(cardId, (card) => {
      if (card.buttons.length >= CAROUSEL_LIMITS.buttonsPerCardMax) return card;
      return { ...card, buttons: [...card.buttons, createButton(CAROUSEL_BUTTON_TYPE.QUICK_REPLY)] };
    });
  };

  const handleRemoveButton = (cardId, buttonId) => {
    updateCard(cardId, (card) => ({ ...card, buttons: card.buttons.filter((btn) => btn.buttonId !== buttonId) }));
  };

  // A type switch always comes from the factory (state.js's own rule for createButton — "never a
  // hand-built object"), so every type-specific field resets to that type's blank shape; only the
  // already-typed button text is worth carrying over.
  const handleButtonTypeChange = (cardId, buttonIndex, newType) => {
    updateCard(cardId, (card) => ({
      ...card,
      buttons: card.buttons.map((btn, i) => (i === buttonIndex ? { ...createButton(newType), text: btn.text } : btn)),
    }));
  };

  const handleButtonFieldChange = (cardId, buttonId, patch) => {
    updateCard(cardId, (card) => ({
      ...card,
      buttons: card.buttons.map((btn) => (btn.buttonId === buttonId ? { ...btn, ...patch } : btn)),
    }));
  };

  // Shared by both buttonValue fields below (URL/PHONE_NUMBER) — computes the live error for
  // btn's own type via checkerByType and writes it in the same patch as the value.
  // DYNAMIC url buttons get one extra check on top — same rule as validate.js's submit-time check.
  const handleButtonValueChange = (cardId, btn, value) => {
    const checker = checkerByType[btn.type];
    let error = checker ? checker(value) : '';
    if (!error && btn.type === CAROUSEL_BUTTON_TYPE.URL && btn.urlType?.value === URL_TYPE.DYNAMIC) {
      error = checkDynamicUrlBase(value);
    }
    handleButtonFieldChange(cardId, btn.buttonId, { buttonValue: { value, error } });
  };

  const errorKeys = Object.keys(errors);
  const cardHasError = (index) => {
    const prefix = `carousels.cards.${index}.`;
    return errorKeys.some((key) => key.startsWith(prefix));
  };

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-carousel">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-carousel-heading">
            Carousel*
          </Typography>
        </div>
      </div>
      {/* console's own copy misspells this "desrciptions" — fixed here on purpose. */}
      <Typography variant="caption" className="br-tpl-form-helper-text">
        Add media, buttons and card descriptions for your carousel cards
      </Typography>

      <Select
        label="Header media type"
        items={MEDIA_TYPE_OPTIONS}
        value={state.carousels.mediaType}
        handleChange={handleMediaTypeChange}
        disabled={disabled}
        width="100%"
        data-testid="bloomreach-templates-form-carousel-media-type-select"
      />

      {/* Defensive backstop for validate.js's own carousels.cards.length === 0 check — see that
          check's own comment. The mount effect above is what actually keeps `cards` from ever
          being empty by the time a user reaches this render, so this should be unreachable in
          practice, same as AuthAppSetup.js's matching errors.appsSetup block. */}
      {errors['carousels.cards'] && (
        <Typography
          variant="caption"
          color="error"
          data-testid="bloomreach-templates-form-carousel-cards-error"
        >
          {friendlyError(errors['carousels.cards'])}
        </Typography>
      )}

      <div className="br-tpl-form-carousel-cards" data-testid="bloomreach-templates-form-carousel-cards">
        {cards.map((card, index) => {
          const mediaRule = MEDIA_RULES[state.carousels.mediaType];
          const isUploading = Boolean(uploadingCardIds[card.cardId]);
          const isMediaStale = Boolean(
            card.mediaUploadedAt && (Date.now() - card.mediaUploadedAt) > MEDIA_EXPIRY_WARNING_MS,
          );
          const isExpanded = expandedCardId === undefined ? index === 0 : expandedCardId === card.cardId;
          // This card's slot in `errors` as of the last validateForm() pass — see
          // cardErrorIndexFor's own comment above. Resolved once per card and reused below for
          // every errors[...] lookup (media, description, buttons) so they all agree on the same
          // slot.
          const cardErrorIndex = cardErrorIndexFor(card.cardId);
          const hasError = cardHasError(cardErrorIndex);
          // Precise (not the console's hardcoded "-5" approximation, CarouselEditor.tsx:138) since
          // the real next token is already known here — a card past nine variables would need a
          // 6-char "{{10}}", not 5, and this checks the actual token's length either way.
          const nextCardVariableTokenValue = nextCardVariableToken(card.description.value);
          const isAddCardVariableDisabled = disabled
            || wouldExceedCardMax(card.description.value, nextCardVariableTokenValue);
          // Console puts the card's delete control in its heading, not its body (CarouselsCard.tsx
          // :422-432) — same minimum-card rule as before (CAROUSEL_LIMITS.cardsMin), just gating an
          // icon instead of a text button now.
          const canRemoveCard = !disabled && cards.length > CAROUSEL_LIMITS.cardsMin;

          const heading = (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            }}
            >
              {/* Drag handle — visible whether the card is collapsed or expanded, same as gs-ui-
                  templates' own drag icon placement, and the only draggable element on the card
                  (see handleCardDragStart's own comment for why). No dedicated CSS class: cursor
                  is the only styling it needs, and it's a one-off, same as this row's own layout. */}
              <span
                draggable={!disabled}
                onDragStart={(event) => handleCardDragStart(event, card.cardId)}
                onDragEnd={handleCardDragEnd}
                style={{ display: 'inline-flex', cursor: disabled ? 'default' : 'grab' }}
                title="Drag to reorder"
                aria-label={`Drag to reorder card ${index + 1}`}
                data-testid={`bloomreach-templates-form-carousel-card-${index}-drag-handle`}
              >
                <Icon name="dotsThreeVertical" size={18} />
              </span>
              <div className="br-tpl-form-carousel-card-heading">
                <Typography variant="body2" fontWeight={600} color={hasError ? 'error' : undefined}>
                  {`Card ${index + 1}`}
                </Typography>
                {card.description.value && (
                  <Typography variant="caption" className="br-tpl-form-helper-text" noWrap>
                    {card.description.value}
                  </Typography>
                )}
              </div>
              {/* Console-exact control (CarouselsCard.tsx:422-432, name='delete') replacing our own
                  "Remove card" button that used to sit in the card body below. stopPropagation
                  keeps the click from also toggling the Accordion this heading belongs to. */}
              <Icon
                name="delete"
                alt="Remove card"
                size={18}
                showCursor={canRemoveCard}
                iconStyles={{ marginLeft: 'auto', opacity: canRemoveCard ? 1 : 0.4 }}
                onClick={canRemoveCard ? (event) => {
                  event.stopPropagation();
                  handleRemoveCard(card.cardId);
                } : undefined}
                dataTestId={`bloomreach-templates-form-carousel-card-${index}-remove`}
              />
            </div>
          );

          const content = (
            <div className="br-tpl-form-carousel-card-body">
              <Typography variant="caption" className="br-tpl-form-helper-text">Card media</Typography>
              {card.cardMedia.file ? (
                <>
                  <CarouselCardMediaPreview
                    file={card.cardMedia.file}
                    mediaSrc={card.cardMedia.mediaSrc}
                    mediaType={state.carousels.mediaType}
                    index={index}
                  />
                  <div className="br-tpl-form-media-picked">
                    <Typography
                      variant="body2"
                      noWrap
                      data-testid={`bloomreach-templates-form-carousel-card-${index}-media-filename`}
                    >
                      {card.cardMedia.file.name}
                    </Typography>
                    {isUploading ? (
                      <Typography
                        variant="caption"
                        data-testid={`bloomreach-templates-form-carousel-card-${index}-media-uploading`}
                      >
                        Uploading…
                      </Typography>
                    ) : (
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        disabled={disabled}
                        onClick={() => handleRemoveMedia(card.cardId)}
                        data-testid={`bloomreach-templates-form-carousel-card-${index}-media-remove`}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Edit-mode only: no file picked yet, but this card already has media — show
                      what's live today so the user isn't editing blind. Enterprise still requires
                      a fresh upload on every media edit (validate.js), so the FileInput below
                      stays mandatory regardless — this is informational, not a substitute for it. */}
                  {card.cardMedia.mediaSrc && (
                    <CarouselCardMediaPreview
                      file={null}
                      mediaSrc={card.cardMedia.mediaSrc}
                      mediaType={state.carousels.mediaType}
                      index={index}
                      currentMediaCaption="Current media — upload a file to keep or replace it"
                    />
                  )}
                  <FileInput
                    fullWidth
                    inputName={`carousel_card_media_${index}`}
                    fileType={(mediaRule?.accept || []).join(',')}
                    showLoader={isUploading}
                    handleFiles={(files) => handleCardFileChange(card.cardId, files)}
                    disabled={disabled || isUploading}
                    error={Boolean(card.cardMedia.error)}
                    defaultText={`File size should be max ${spacedMaxSizeLabel(mediaRule?.maxSizeLabel)}`}
                    existingSelectedFiles={[]}
                    data-testid={`bloomreach-templates-form-carousel-card-${index}-media-fileinput`}
                  />
                </>
              )}
              {card.cardMedia.error && (
                <Typography
                  variant="caption"
                  color="error"
                  data-testid={`bloomreach-templates-form-carousel-card-${index}-media-error`}
                >
                  {card.cardMedia.error}
                </Typography>
              )}
              {errors[`carousels.cards.${cardErrorIndex}.cardMedia`] && (
                <Typography
                  variant="caption"
                  color="error"
                  data-testid={`bloomreach-templates-form-carousel-card-${index}-media-id-error`}
                >
                  {friendlyError(errors[`carousels.cards.${cardErrorIndex}.cardMedia`])}
                </Typography>
              )}
              {isMediaStale && (
                <>
                  <Alert
                    type="warning"
                    description="Uploaded media expires after 30 minutes — re-upload if you haven't submitted yet."
                    data-testid={`bloomreach-templates-form-carousel-card-${index}-media-expiry-warning`}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={disabled || isUploading || !card.cardMedia.file}
                    onClick={() => uploadCardMedia(card.cardId, card.cardMedia.file)}
                    data-testid={`bloomreach-templates-form-carousel-card-${index}-media-reupload`}
                  >
                    Re-upload
                  </Button>
                </>
              )}

              {/* card.variable is kept in sync with the description's own {{n}} tokens by the
                  token-watching effect above (cardVariableSyncKey) — same approach
                  sections/Variables.js uses for header/body. Add-variable below still only
                  inserts the {{n}} token into the text itself, same as typing it by hand; the
                  effect is what turns that into a sample-value row below, either way. */}
              <TextField
                fullWidth
                value={card.description.value}
                onChange={(event) => handleDescriptionChange(card.cardId, event.target.value)}
                disabled={disabled}
                placeholder="Enter Description"
                autoComplete="off"
                inputRef={(el) => { descriptionRefs.current.set(card.cardId, el); }}
                inputProps={{
                  maxLength: CAROUSEL_LIMITS.cardBodyMaxChars,
                  'data-testid': `bloomreach-templates-form-carousel-card-${index}-description-textfield`,
                }}
                {...errorProps(errors, `carousels.cards.${cardErrorIndex}.description.value`)}
              />
              <div className="br-tpl-form-carousel-description-actions">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon="plus"
                  disabled={isAddCardVariableDisabled}
                  onClick={() => handleAddCardVariable(card.cardId)}
                  data-testid={`bloomreach-templates-form-carousel-card-${index}-description-add-variable`}
                >
                  Add variable
                </Button>
                <EmojiPickerButton
                  onEmojiSelect={(emoji) => handleCardEmojiSelect(card.cardId, emoji)}
                  disabled={disabled}
                  dataTestId={`bloomreach-templates-form-carousel-card-${index}-description-emoji`}
                />
                <Typography
                  variant="caption"
                  className="br-tpl-form-char-count"
                  data-testid={`bloomreach-templates-form-carousel-card-${index}-description-count`}
                >
                  {`${card.description.value.length} / ${CAROUSEL_LIMITS.cardBodyMaxChars}`}
                </Typography>
              </div>

              {/* One sample-value input per card.variable row — same shape/wording as the
                  top-level Variables card (sections/Variables.js: "Enter sample variable value"
                  placeholder, {{n}} startAdornment) and as the console's own CAROUSEL-field
                  Variables render (gs-ui-templates Common/Variables.tsx skips a heading for this
                  field specifically, just the rows — mirrored here, no heading either).
                  cardErrorIndex, not the live `index`, so a sample's error follows its card
                  across a drag reorder, same rule every other error lookup on this card already
                  follows. */}
              {card.variable.length > 0 && (
                <div
                  className="br-tpl-form-variable-rows"
                  data-testid={`bloomreach-templates-form-carousel-card-${index}-variable-rows`}
                >
                  {card.variable.map((row, varIndex) => (
                    <div className="br-tpl-form-variable-row" key={`${card.cardId}-${row.name}`}>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.value}
                        placeholder="Enter sample variable value"
                        disabled={disabled}
                        autoComplete="off"
                        onChange={(event) => handleCardVariableChange(card.cardId, varIndex, event.target.value)}
                        InputProps={{
                          startAdornment: <Typography className="br-tpl-form-variable-token">{row.name}</Typography>,
                        }}
                        inputProps={{
                          'data-testid': `bloomreach-templates-form-carousel-card-${index}-variable-${varIndex}`,
                        }}
                        {...errorProps(errors, `carousels.cards.${cardErrorIndex}.variable.${varIndex}.value`)}
                      />
                    </div>
                  ))}
                </div>
              )}

              {card.buttons.map((btn, btnIndex) => {
                const btnPath = `carousels.cards.${cardErrorIndex}.buttons.${btnIndex}`;
                const isUrl = btn.type === CAROUSEL_BUTTON_TYPE.URL;
                const isPhone = btn.type === CAROUSEL_BUTTON_TYPE.PHONE_NUMBER;
                const isQuickReply = btn.type === CAROUSEL_BUTTON_TYPE.QUICK_REPLY;
                const isDynamic = isUrl && btn.urlType?.value === URL_TYPE.DYNAMIC;
                const isLinkTrack = isUrl && isLinkTracked(btn);

                return (
                  <div
                    className="br-tpl-form-carousel-button-row"
                    key={btn.buttonId}
                    data-testid={`bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}`}
                  >
                    <Select
                      label="Select type"
                      items={buildCardButtonTypeOptions(btn.type)}
                      value={btn.type}
                      handleChange={(selected) => handleButtonTypeChange(
                        card.cardId, btnIndex, Array.isArray(selected) ? selected[0] : selected,
                      )}
                      disabled={disabled}
                      width="100%"
                      data-testid={`bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-type-select`}
                    />
                    <TextField
                      fullWidth
                      label={isQuickReply ? 'Quick Reply Title' : 'Button Title'}
                      value={btn.text.value}
                      onChange={(event) => handleButtonFieldChange(
                        card.cardId, btn.buttonId, { text: { value: event.target.value, error: '' } },
                      )}
                      disabled={disabled}
                      placeholder={isQuickReply ? 'Type quick reply title' : 'Type button title'}
                      autoComplete="off"
                      inputProps={{
                        maxLength: CAROUSEL_LIMITS.buttonTextMaxChars,
                        'data-testid': `bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-text-textfield`,
                      }}
                      {...errorProps(errors, `${btnPath}.text.value`)}
                    />
                    {isUrl && (
                      <>
                        <Select
                          label="Select URL type"
                          items={URL_TYPE_OPTIONS}
                          value={btn.urlType.value}
                          handleChange={(selected) => handleButtonFieldChange(
                            card.cardId,
                            btn.buttonId,
                            { urlType: { value: Array.isArray(selected) ? selected[0] : selected, error: '' } },
                          )}
                          disabled={disabled}
                          width="100%"
                          data-testid={`bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-urltype-select`}
                        />
                        {isLinkTrack ? (
                          // Enterprise fills url/example itself for this type (URLButton.java:
                          // 166-199) — nothing to type here, same as the top-level CTA's own
                          // DYNAMIC_LINK_TRACK button (Buttons.js).
                          <Typography variant="caption" data-testid={`bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-linktrack-note`}>
                            {LINK_TRACKING_NOTE}
                          </Typography>
                        ) : (
                          <>
                            <TextField
                              fullWidth
                              label="Button Value"
                              value={btn.buttonValue.value}
                              onChange={(event) => handleButtonValueChange(card.cardId, btn, event.target.value)}
                              disabled={disabled}
                              placeholder="Type button value"
                              autoComplete="off"
                              inputProps={{
                                maxLength: CAROUSEL_URL_VALUE_MAX_CHARS,
                                'data-testid': `bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-url-textfield`,
                              }}
                              {...liveErrorProps(errors, `${btnPath}.buttonValue.value`, btn.buttonValue.error)}
                            />
                            {isDynamic && (
                              // Mirrors Buttons.js's own top-level dynamic-URL suffix row (the
                              // {{1}} chip + "Type Sample Suffix" + tooltip) — console-exact copy,
                              // console-matching layout, our own validation/payload unchanged.
                              <div
                                className="br-tpl-form-url-suffix-row"
                                data-testid={`bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-suffix-row`}
                              >
                                <span className="br-tpl-form-url-suffix-token">{'{{1}}'}</span>
                                <TextField
                                  fullWidth
                                  value={btn.suffix.value}
                                  onChange={(event) => handleButtonFieldChange(
                                    card.cardId, btn.buttonId, { suffix: { value: event.target.value, error: '' } },
                                  )}
                                  disabled={disabled}
                                  placeholder="Type Sample Suffix"
                                  autoComplete="off"
                                  inputProps={{
                                    'data-testid': `bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-suffix-textfield`,
                                  }}
                                  {...errorProps(errors, `${btnPath}.suffix.value`)}
                                />
                                <Tooltip
                                  placement="top"
                                  title={(
                                    <>
                                      Sample dynamic URL with variable
                                      <br />
                                      Note: Do not add the URL in your sample message body above
                                    </>
                                  )}
                                  data-testid={`bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-suffix-tooltip`}
                                >
                                  <span>
                                    <Icon name="question" size={18} showCursor />
                                  </span>
                                </Tooltip>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {isPhone && (
                      <TextField
                        fullWidth
                        label="Button Value"
                        value={btn.buttonValue.value}
                        onChange={(event) => handleButtonValueChange(card.cardId, btn, event.target.value)}
                        disabled={disabled}
                        placeholder="Type button value"
                        autoComplete="off"
                        inputProps={{
                          maxLength: CAROUSEL_PHONE_VALUE_MAX_CHARS,
                          'data-testid': `bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-phone-textfield`,
                        }}
                        {...liveErrorProps(errors, `${btnPath}.buttonValue.value`, btn.buttonValue.error)}
                      />
                    )}
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      disabled={disabled}
                      onClick={() => handleRemoveButton(card.cardId, btn.buttonId)}
                      data-testid={`bloomreach-templates-form-carousel-card-${index}-button-${btnIndex}-remove`}
                    >
                      Remove button
                    </Button>
                  </div>
                );
              })}

              {errors[`carousels.cards.${cardErrorIndex}.buttons`] && (
                <Typography
                  variant="caption"
                  color="error"
                  data-testid={`bloomreach-templates-form-carousel-card-${index}-buttons-error`}
                >
                  {friendlyError(errors[`carousels.cards.${cardErrorIndex}.buttons`])}
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={disabled || card.buttons.length >= CAROUSEL_LIMITS.buttonsPerCardMax}
                onClick={() => handleAddButton(card.cardId)}
                data-testid={`bloomreach-templates-form-carousel-card-${index}-add-button`}
              >
                Add button
              </Button>
            </div>
          );

          // The drop target is this wrapper, not the Accordion itself — Accordion spreads unknown
          // props onto the underlying MUI Accordion, but it also computes its own className
          // internally and layers the rest object on top, so a className passed in here would
          // replace (not add to) the library's own accordion/grey/readonly classes. A plain div
          // sidesteps that entirely.
          return (
            <div
              key={card.cardId}
              className={dragOverCardId === card.cardId ? 'br-tpl-form-carousel-card--drag-over' : undefined}
              onDragOver={(event) => handleCardDragOver(event, card.cardId)}
              onDrop={(event) => handleCardDrop(event, card.cardId)}
            >
              <Accordion
                heading={heading}
                content={content}
                expanded={isExpanded}
                onChange={(_event, nextExpanded) => setExpandedCardId(nextExpanded ? card.cardId : null)}
                disabled={disabled}
                data-testid={`bloomreach-templates-form-carousel-card-${index}`}
              />
            </div>
          );
        })}
      </div>

      <Button
        size="small"
        variant="outlined"
        startIcon="plus"
        disabled={disabled || cards.length >= CAROUSEL_LIMITS.cardsMax}
        onClick={handleAddCard}
        data-testid="bloomreach-templates-form-carousel-add-card"
      >
        Add another card
      </Button>
    </div>
  );
};

export default CarouselSection;
